import { useState, useEffect, useRef } from 'react';

const useLazyBackgroundImage = (imageUrl, options = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const elementRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(element);
        }
      },
      {
        rootMargin: options.rootMargin || '100px',
        threshold: options.threshold || 0.01,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [options.rootMargin, options.threshold]);

  useEffect(() => {
    if (!isInView || !imageUrl) return;

    // Abort previous image loading if exists
    if (imageRef.current) {
      imageRef.current.onload = null;
      imageRef.current.onerror = null;
    }

    const img = new Image();
    imageRef.current = img;
    
    img.onload = () => {
      setIsLoaded(true);
      setHasError(false);
    };
    img.onerror = () => {
      setHasError(true);
      setIsLoaded(true); // Set loaded even on error to prevent infinite loading
    };
    
    // Set decoding for better performance
    if ('decoding' in img) {
      img.decoding = 'async';
    }
    
    img.src = imageUrl;

    return () => {
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
      }
    };
  }, [isInView, imageUrl]);

  return { elementRef, isLoaded, isInView, hasError };
};

export default useLazyBackgroundImage;
